export interface PublicDetailFetchError {
  type: 'search_error' | 'not_found';
  statusCode: number;
  message: string;
  originalError: unknown;
}

interface UsePublicDetailFetchInput<TData> {
  /** `useAsyncData` cache key. Include any variant suffix (e.g. `-preview`) in the key. */
  key: string;
  /** Executes the query. Must throw on a transport or GraphQL error. */
  fetch: () => Promise<TData>;
  /** Whether a successfully resolved payload contains no matching record. */
  isNotFound: (data: TData | null) => boolean;
  /** Builds the `originalError` attached to the 404 (names the missing slug/id). */
  notFoundError: () => Error;
}

/**
 * Shared fetch shape for public SSR detail pages (article, industry, job): a blocking server
 * fetch that hard-fails with a real 404/500 for crawlers, and a deferred client fetch that
 * keeps SPA navigation unblocked.
 */
export const usePublicDetailFetch = async <TData>(input: UsePublicDetailFetchInput<TData>) => {
  const { t } = useI18n();

  const { data, pending: fetching, error: queryError, status } = await useAsyncData(
    input.key,
    input.fetch,
    {
      // `lazy: true` keeps the SSR fetch blocking (the server ignores `lazy`, so the awaited
      // promise still resolves with `error` populated for the throw below) while deferring the
      // client fetch, so client-side navigation isn't blocked and the page's `fetching` loader
      // still shows.
      server: true,
      lazy: true,
    },
  );

  const error = computed((): PublicDetailFetchError | null => {
    if (queryError.value) {
      return {
        type: 'search_error',
        statusCode: 500,
        message: t('unified.errors.genericFailed'),
        originalError: queryError.value,
      };
    }

    // Gate on resolution, not pending: with `lazy`, a client-side navigation resolves the
    // awaited promise while the fetch is still `idle` (`pendingWhenIdle` is false), so a
    // `!fetching` gate would report a false 404 before the fetch even starts.
    // `useAsyncData` types its ref as `PickFrom<TData, ...>`, which TS can't unify with TData
    // for an arbitrary generic; it is the same shape at runtime.
    if (status.value === 'success' && input.isNotFound((data.value ?? null) as TData | null)) {
      return {
        type: 'not_found',
        statusCode: 404,
        message: t('unified.errors.pageNotFound'),
        originalError: input.notFoundError(),
      };
    }

    return null;
  });

  // Hard-fail server-side so crawlers get a real 404/500 instead of a 200 "not found" shell
  // (audit M5). The await above guarantees the fetch has resolved; the page's client-side
  // showError still covers SPA navigations.
  throwOnServerFetchError(error.value);

  return { data, fetching, error };
};
